import { useCallback, useEffect, useRef, useState } from 'react';

// Sub-pixel band: a section landed exactly at its scroll-margin-top still counts as reached.
const SECTION_TOP_EPSILON = 2;

// Once scroll events stop for this long, a smooth scroll has settled and the lock can release.
const SCROLL_SETTLE_DELAY = 150;

// Fallback release for a lock whose target was already in place (no scroll events follow).
// Long enough that a slow-to-start smooth scroll keeps its lock; the first scroll event
// swaps in the shorter settle timer.
const LOCK_FALLBACK_RELEASE_DELAY = 1000;

const PAGE_END_EPSILON = 2;

interface UseSectionScrollSpyOptions {
  // An element rather than a ref so callers whose content mounts late (behind a
  // query boundary) can pass it as state; the spy attaches once it exists.
  content: HTMLElement | null;
  sectionIds: string[];
}

interface ResolvedSection {
  id: string;
  element: HTMLElement;
}

/**
 * Every section shares `scroll-mt-(--scroll-offset)`, so one read off a reference
 * section gives the offset used to place the observer's trigger line.
 */
function readSharedOffset(reference: HTMLElement | undefined): number {
  if (!reference) {
    return 0;
  }
  return Number.parseFloat(getComputedStyle(reference).scrollMarginTop) || 0;
}

/**
 * Collapses the observer root to a ~1px line at the shared offset, so a section
 * reports a crossing as its top passes where scrollIntoView lands jump-links.
 */
function buildRootMargin(offset: number, viewportHeight: number): string {
  const bottom = Math.max(0, viewportHeight - offset - 1);
  return `-${offset}px 0px -${bottom}px 0px`;
}

/**
 * Tracks the active page section for a section navigator.
 *
 * Active = the last section whose top has passed its `scroll-margin-top` (the
 * offset scrollIntoView lands jump-links at). An IntersectionObserver pinned to
 * that offset line triggers the recompute only at crossings, not every frame; a
 * light scroll listener rebuilds the observer when the sticky offset drifts and
 * owns the page-bottom clamp and the selection lock. Sections are looked up in
 * `content` (a shadow root hides them from document-level lookups); ids that
 * are not mounted yet are skipped and picked up by a MutationObserver once
 * they arrive. `lockActiveSection` pins the highlight on a jump-link click and
 * suppresses spy updates until the smooth scroll settles. The resolved set is
 * published as `presentSectionIds` for navigators that hide absent sections.
 */
export function useSectionScrollSpy({
  content,
  sectionIds,
}: UseSectionScrollSpyOptions) {
  const [activeSection, setActiveSection] = useState<string | undefined>(
    undefined,
  );
  // `undefined` until the first resolve pass lands; callers fall back to all ids.
  const [resolvedSectionIds, setResolvedSectionIds] = useState<
    string[] | undefined
  >(undefined);
  const lockRef = useRef(false);
  const settleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sectionIdsKey = sectionIds.join(' ');

  const lockActiveSection = useCallback((sectionId: string) => {
    lockRef.current = true;
    setActiveSection(sectionId);

    // Target already in place → no scroll events, so release on our own; the
    // first scroll event swaps this for the shorter settle timer.
    if (settleTimeoutRef.current) {
      clearTimeout(settleTimeoutRef.current);
    }
    settleTimeoutRef.current = setTimeout(() => {
      lockRef.current = false;
    }, LOCK_FALLBACK_RELEASE_DELAY);
  }, []);

  useEffect(() => {
    if (!content || typeof window === 'undefined') {
      return;
    }

    const ids = sectionIdsKey.split(' ').filter(Boolean);

    // A new content column resolves to a different set.
    setResolvedSectionIds(undefined);

    // `syncSections` runs on every crossing; only publish when the ids change,
    // so the nav does not re-render for each one.
    let publishedIdsKey: string | null = null;

    const publishSectionIds = (resolved: ResolvedSection[]) => {
      const publishedIds = resolved.map(({ id }) => id);
      const key = publishedIds.join(' ');

      if (key === publishedIdsKey) {
        return;
      }

      publishedIdsKey = key;
      setResolvedSectionIds(publishedIds);
    };

    const resolveSections = (): ResolvedSection[] =>
      ids.flatMap((id) => {
        const element = content.querySelector<HTMLElement>(`#${id}`);
        return element ? [{ id, element }] : [];
      });

    let sections = resolveSections();

    // Last section past the offset line, pre-clamp. Recomputed from the DOM only
    // at crossings (and offset/viewport changes); the per-frame path reuses it.
    let baseActiveId: string | undefined = sections[0]?.id;

    // Cached to detect when the moving sticky stack shifts the trigger line.
    let offset = readSharedOffset(sections[0]?.element);
    let viewportHeight = window.innerHeight;

    let frameId: number | null = null;
    let needsBaseRecompute = false;
    let observer: IntersectionObserver | null = null;

    // Sections both appear (one mounts behind its own query boundary) and
    // disappear (data-driven `null`), so compare the resolved elements rather
    // than only checking for detached ones, and re-point the observer on change.
    const syncSections = () => {
      const next = resolveSections();
      const changed =
        next.length !== sections.length ||
        next.some(({ element }, index) => sections[index]?.element !== element);

      sections = next;

      if (changed) {
        rebuildObserver();
      }

      publishSectionIds(sections);
    };

    const computeBaseActive = () => {
      syncSections();

      const [firstSection] = sections;

      if (!firstSection) {
        baseActiveId = undefined;
        return;
      }

      let next = firstSection.id;

      for (const { id, element } of sections) {
        const sectionOffset =
          Number.parseFloat(getComputedStyle(element).scrollMarginTop) || 0;

        if (
          element.getBoundingClientRect().top <=
          sectionOffset + SECTION_TOP_EPSILON
        ) {
          next = id;
        }
      }

      baseActiveId = next;
    };

    const applyActive = () => {
      const [firstSection] = sections;

      if (!firstSection) {
        setActiveSection(undefined);
        return;
      }

      let next = baseActiveId ?? firstSection.id;

      // Short trailing sections may never reach the offset line, so the page
      // bottom counts as the last section. The scrollY guard keeps a short page
      // (already at its bottom at scrollY 0) from forcing it active at the top.
      const pageBottom = window.innerHeight + window.scrollY;
      if (
        window.scrollY > 0 &&
        pageBottom >= document.documentElement.scrollHeight - PAGE_END_EPSILON
      ) {
        next = sections[sections.length - 1]?.id ?? next;
      }

      setActiveSection(next);
    };

    const rebuildObserver = () => {
      observer?.disconnect();

      if (sections.length === 0 || typeof IntersectionObserver !== 'function') {
        observer = null;
        return;
      }

      observer = new IntersectionObserver(
        () => {
          // Entries only signal *that* something crossed; the active section is
          // read from the DOM, staying correct even if a rebuild lags a frame.
          scheduleSync(true);
        },
        { root: null, rootMargin: buildRootMargin(offset, viewportHeight) },
      );

      for (const { element } of sections) {
        observer.observe(element);
      }
    };

    const runSync = () => {
      frameId = null;

      // Single lock choke point: pending frames, the observer, and resize all
      // land here, and none may retarget mid-scroll.
      if (lockRef.current) {
        needsBaseRecompute = false;
        return;
      }

      // Realign the trigger line when the sticky stack (offset/viewport) moves.
      const currentOffset = readSharedOffset(sections[0]?.element);
      const currentViewportHeight = window.innerHeight;
      if (
        currentOffset !== offset ||
        currentViewportHeight !== viewportHeight
      ) {
        offset = currentOffset;
        viewportHeight = currentViewportHeight;
        rebuildObserver();
        needsBaseRecompute = true;
      }

      if (needsBaseRecompute) {
        needsBaseRecompute = false;
        computeBaseActive();
      }

      applyActive();
    };

    const scheduleSync = (recomputeBase: boolean) => {
      if (recomputeBase) {
        needsBaseRecompute = true;
      }
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(runSync);
    };

    const handleScroll = () => {
      if (lockRef.current) {
        if (settleTimeoutRef.current) {
          clearTimeout(settleTimeoutRef.current);
        }
        settleTimeoutRef.current = setTimeout(() => {
          lockRef.current = false;
          scheduleSync(true);
        }, SCROLL_SETTLE_DELAY);
        return;
      }

      // O(1) per-frame path; the observer recomputes base at crossings. Without
      // IntersectionObserver, recompute from scroll so the spy still tracks.
      scheduleSync(observer === null);
    };

    const handleResize = () => scheduleSync(true);

    // A section that mounts late never crosses the observer's line, because the
    // observer isn't watching it yet. rAF coalesces the churn from tabs and
    // tables into at most one pass per frame.
    const contentObserver = new MutationObserver(() => scheduleSync(true));
    contentObserver.observe(content, { childList: true, subtree: true });

    rebuildObserver();
    // Seed the initial highlight from the freshly mounted content.
    scheduleSync(true);

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      if (settleTimeoutRef.current) {
        clearTimeout(settleTimeoutRef.current);
      }
      observer?.disconnect();
      contentObserver.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [content, sectionIdsKey]);

  return {
    activeSection,
    lockActiveSection,
    presentSectionIds: resolvedSectionIds ?? sectionIds,
  };
}
